import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { UserRound } from 'lucide-react';
import { AdAccount, createIndividualAdAccount } from '../../api/ads/ad-account.api';
import { EkycCameraModal } from '../../pages/donate-goal/ekyc-camera-modal';
import { routePaths } from '../../routes/routes-path';
import { apiErrorMessage } from '../../utils/api-error-message';
import { apiErrorCode, ERROR_CODE, formatCa } from '../../utils/individual-order';

interface Props {
  /** Tài khoản đặt đơn cá nhân hiện có (null = chưa mở). */
  account: AdAccount | null;
  /** Ca nạp khả dụng (null = chưa tải được). */
  topUpCa: number | null;
  onCreated: (account: AdAccount) => void;
}

// Cá nhân đã định danh đặt đơn với creator bằng Ca nạp (spec 2026-10-09 §3). Không cần giấy tờ doanh nghiệp.
const IndividualOrdererCard: React.FC<Props> = ({ account, topUpCa, onCreated }) => {
  const [busy, setBusy] = useState(false);
  const [ekycOpen, setEkycOpen] = useState(false);

  const create = async () => {
    try {
      setBusy(true);
      const created = await createIndividualAdAccount();
      toast.success('Đã mở tài khoản đặt đơn cá nhân. Bạn có thể mời creator ngay.');
      onCreated(created);
    } catch (e) {
      if (apiErrorCode(e) === ERROR_CODE.ekycRequired) {
        setEkycOpen(true);
        toast(apiErrorMessage(e, 'Bạn cần định danh (eKYC) trước khi đặt đơn.'), { icon: '🪪' });
        return;
      }
      toast.error(apiErrorMessage(e, 'Chưa mở được tài khoản đặt đơn cá nhân, thử lại sau.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl ui-divider border p-4 space-y-2">
      <h2 className="font-semibold ui-text flex items-center gap-2">
        <UserRound size={18} className="text-[#0071A6]" /> Đặt đơn với tư cách cá nhân
      </h2>
      {account ? (
        <>
          <p className="text-xs ui-muted">
            Tài khoản đặt đơn cá nhân của <b className="ui-text">{account.businessName}</b>. Tiền đặt đơn được giữ từ Ca nạp và
            hoàn về Ca nạp nếu đơn không thành.
          </p>
          <div className="flex items-center justify-between rounded-lg bg-[#f6fbff] dark:bg-slate-800 px-3 py-2">
            <span className="text-sm ui-muted">Ca nạp khả dụng</span>
            <span className="flex items-center gap-3">
              <span className="text-lg font-bold text-[#0071A6] dark:text-[#48afff]">
                {topUpCa === null ? '…' : `${formatCa(topUpCa)} Ca`}
              </span>
              <Link to={routePaths.wallet} className="rounded-md bg-[#0071A6] px-3 py-1.5 text-sm text-white hover:brightness-110">
                Nạp Ca
              </Link>
            </span>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs ui-muted">
            Thuê creator đăng bài, làm nội dung hoặc gắn quảng cáo lên livestream bằng Ca nạp của bạn. Chỉ cần định danh
            (eKYC), không cần giấy tờ doanh nghiệp.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={create}
            className="rounded-md bg-[#0071A6] text-white text-sm px-3 py-1.5 hover:brightness-110 disabled:opacity-50"
          >
            Đặt đơn với tư cách cá nhân
          </button>
        </>
      )}
      <EkycCameraModal
        open={ekycOpen}
        identityOnly
        onClose={() => setEkycOpen(false)}
        onVerified={() => {
          setEkycOpen(false);
          create();
        }}
      />
    </div>
  );
};

export default IndividualOrdererCard;
